import React, { useState } from "react";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";

import { ApplicationLayout } from "../components/funding/ApplicationLayout";
import {
    FormField,
    FormSelect,
    FormTextarea,
    FormSuccess,
} from "../components/zen/FormField";
import { FileDropzone } from "../components/ui/FileDropzone";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISCIPLINES = [
    "Architecture",
    "Structural",
    "MEP",
    "HVAC",
    "Electrical",
    "Plumbing",
    "BIM",
    "Multi-discipline / Other",
];

const PROJECT_SIZES = [
    "Under 10,000 sq ft",
    "10,000 – 50,000 sq ft",
    "50,000 – 250,000 sq ft",
    "250,000 sq ft+",
    "Not specified",
];

const TIMELINES = [
    "Immediate (within 30 days)",
    "1 – 3 months",
    "3 – 6 months",
    "6+ months",
    "Ongoing support",
];

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRe = /^[+\-\(\)\s\d]{7,30}$/;

const REQUIRED = [
    "company_name",
    "contact_name",
    "email",
    "phone",
    "discipline",
    "project_type",
    "project_size",
    "timeline",
];

const INITIAL = {
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    discipline: "",
    project_type: "",
    project_size: "",
    timeline: "",
    message: "",
};

export default function BimInquiry() {
    const [values, setValues] = useState(INITIAL);
    const [files, setFiles] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    const onChange = (name, value) => {
        setValues((v) => ({ ...v, [name]: value }));
        setErrors((e) => {
            if (!e[name]) return e;
            const { [name]: _, ...rest } = e;
            return rest;
        });
        if (submitError) setSubmitError(null);
    };

    const validate = () => {
        const errs = {};
        for (const f of REQUIRED) {
            const v = String(values[f] || "").trim();
            if (!v) errs[f] = "Required";
        }
        if (values.email && !emailRe.test(values.email)) {
            errs.email = "Please enter a valid email.";
        }
        if (values.phone && !phoneRe.test(values.phone)) {
            errs.phone = "Please enter a valid phone number.";
        }
        if (values.company_name && values.company_name.trim().length < 2) {
            errs.company_name = "Please enter at least 2 characters.";
        }
        if (values.contact_name && values.contact_name.trim().length < 2) {
            errs.contact_name = "Please enter at least 2 characters.";
        }
        if (values.message && values.message.length > 2000) {
            errs.message = "Message must be under 2000 characters.";
        }
        return errs;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        const errs = validate();
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;

        setSubmitting(true);
        setSubmitError(null);
        try {
            const fd = new FormData();
            for (const key of Object.keys(INITIAL)) {
                fd.append(key, values[key] || "");
            }
            for (const f of files) {
                fd.append("files", f, f.name);
            }
            await axios.post(`${API}/leads/bim`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setSuccess(true);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const message =
                typeof detail === "string"
                    ? detail
                    : "Something went wrong submitting your inquiry. Please try again.";
            setSubmitError(message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <ApplicationLayout>
            <div data-testid="bim-inquiry-page" className="scroll-mt-24">
                <div className="mb-8">
                    <p className="text-[11px] uppercase tracking-widest-2 text-text-muted">
                        BIM & Engineering
                    </p>
                    <h1 className="mt-3 font-display text-[clamp(28px,4vw,42px)] font-semibold tracking-tight-2 text-text-primary">
                        Tell us about your project.
                    </h1>
                    <p className="mt-3 max-w-xl text-[15.5px] leading-[1.65] text-text-secondary">
                        Share a few details and any drawings. A U.S.-based
                        ZenMe project lead will reach out shortly to schedule a
                        discovery call.
                    </p>
                </div>

                <div
                    className="rounded-2xl border border-border-subtle bg-elevated p-6 sm:p-10"
                    style={{
                        boxShadow:
                            "0 24px 48px -24px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(255,255,255,0.01)",
                    }}
                    data-testid="bim-inquiry-card"
                >
                    {success ? (
                        <FormSuccess
                            title="Received."
                            message="Thanks — your inquiry has been received. A ZenMe project lead will reach out shortly to schedule a call."
                            data-testid="bim-inquiry-success"
                        />
                    ) : (
                        <form
                            onSubmit={onSubmit}
                            noValidate
                            className="grid grid-cols-1 gap-6 sm:grid-cols-2"
                            data-testid="bim-inquiry-form"
                        >
                            {submitError && (
                                <div
                                    role="alert"
                                    data-testid="bim-inquiry-error"
                                    className="sm:col-span-2 flex items-start gap-3 rounded-lg border border-[rgba(248,113,113,0.25)] bg-[rgba(248,113,113,0.06)] px-4 py-3"
                                >
                                    <AlertTriangle
                                        className="h-4 w-4 shrink-0 text-red-400 mt-0.5"
                                        strokeWidth={1.75}
                                    />
                                    <p className="text-[13.5px] leading-snug text-red-300/90">
                                        {submitError}
                                    </p>
                                </div>
                            )}

                            <FormField
                                label="Company Name"
                                name="company_name"
                                required
                                value={values.company_name}
                                onChange={onChange}
                                error={errors.company_name}
                                placeholder="Acme Engineering Inc."
                                data-testid="bim-input-company"
                            />
                            <FormField
                                label="Contact Name"
                                name="contact_name"
                                required
                                value={values.contact_name}
                                onChange={onChange}
                                error={errors.contact_name}
                                placeholder="Jane Doe"
                                data-testid="bim-input-contact"
                            />
                            <FormField
                                label="Email"
                                name="email"
                                type="email"
                                required
                                value={values.email}
                                onChange={onChange}
                                error={errors.email}
                                placeholder="you@company.com"
                                data-testid="bim-input-email"
                            />
                            <FormField
                                label="Phone"
                                name="phone"
                                type="tel"
                                required
                                value={values.phone}
                                onChange={onChange}
                                error={errors.phone}
                                placeholder="(555) 555-5555"
                                data-testid="bim-input-phone"
                            />
                            <FormSelect
                                label="Discipline Needed"
                                name="discipline"
                                required
                                value={values.discipline}
                                onChange={onChange}
                                error={errors.discipline}
                                options={DISCIPLINES}
                                data-testid="bim-input-discipline"
                            />
                            <FormField
                                label="Project Type"
                                name="project_type"
                                required
                                value={values.project_type}
                                onChange={onChange}
                                error={errors.project_type}
                                placeholder="e.g., Healthcare facility, Data center, Warehouse renovation"
                                data-testid="bim-input-project-type"
                            />
                            <FormSelect
                                label="Approximate Project Size"
                                name="project_size"
                                required
                                value={values.project_size}
                                onChange={onChange}
                                error={errors.project_size}
                                options={PROJECT_SIZES}
                                data-testid="bim-input-project-size"
                            />
                            <FormSelect
                                label="Timeline"
                                name="timeline"
                                required
                                value={values.timeline}
                                onChange={onChange}
                                error={errors.timeline}
                                options={TIMELINES}
                                data-testid="bim-input-timeline"
                            />
                            <div className="sm:col-span-2">
                                <FormTextarea
                                    label="Message"
                                    name="message"
                                    rows={5}
                                    value={values.message}
                                    onChange={onChange}
                                    error={errors.message}
                                    placeholder="Anything else we should know — project context, deliverables, standards, deadlines, etc."
                                    data-testid="bim-input-message"
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <FileDropzone
                                    files={files}
                                    onChange={setFiles}
                                    data-testid="bim-dropzone"
                                />
                            </div>
                            <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-3 border-t border-border-subtle pt-6">
                                <p className="text-[12.5px] text-text-muted">
                                    We respond within one business day.
                                </p>
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    data-testid="bim-submit-btn"
                                    className="zen-glow-on-hover inline-flex items-center justify-center gap-2 rounded-full bg-[var(--accent)] px-7 py-3 text-[14.5px] font-medium text-[#0a0a0b] transition-all duration-300 ease-luxury hover:brightness-110 disabled:opacity-60 disabled:pointer-events-none w-full sm:w-auto"
                                >
                                    {submitting && (
                                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                                    )}
                                    {submitting ? "Submitting…" : "Submit Inquiry"}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </ApplicationLayout>
    );
}
